import { isFundItemContent, ListContent } from "components/context/ListContext";

export type ListSortMode = "alphabetical" | "subAsset";

// Gruppi del sub asset nell'ordine di visualizzazione richiesto.
// "label" è la chiave di traduzione, "superCategories" i valori di Fund.superCategory che vi ricadono.
export const SUB_ASSET_GROUPS: { label: string; superCategories: string[] }[] = [
  { label: "Azionari", superCategories: ["azionari"] },
  { label: "Diversificati", superCategories: ["diversificati"] },
  { label: "Materie prime", superCategories: ["commodities", "materie prime"] },
  { label: "Monetari", superCategories: ["monetari"] },
  { label: "Obbligazionari", superCategories: ["obbligazionari"] },
  { label: "Ritorno Assoluto", superCategories: ["ritorno assoluto"] },
];
// Tutto ciò che non ricade nei gruppi sopra (portafogli compresi) finisce qui, in fondo.
export const OTHER_SUB_ASSET_LABEL = "Altri";

// Una riga della lista visualizzata: intestazione di gruppo oppure voce della lista.
// "entry" è l'elemento ricevuto dal server (fondo/portafoglio); entry.item ne contiene tipo e id.
export type ListRow =
  | { kind: "groupHeader"; label: string; count: number }
  | { kind: "entry"; entry: ListContent };

function getName(entry: ListContent): string {
  return "name" in entry && typeof entry.name === "string" ? entry.name : "";
}

function getSubAssetGroup(entry: ListContent): string {
  if (!isFundItemContent(entry)) return OTHER_SUB_ASSET_LABEL;
  const superCategory = (entry.superCategory ?? "").trim().toLowerCase();
  const group = SUB_ASSET_GROUPS.find((g) => g.superCategories.includes(superCategory));
  return group?.label ?? OTHER_SUB_ASSET_LABEL;
}

/**
 * Costruisce le righe da mostrare in una lista: le voci in ordine alfabetico oppure,
 * per sub asset, raggruppate (con intestazione di gruppo) e alfabetiche dentro ogni gruppo.
 */
export function buildListRows(
  listEntries: ListContent[],
  mode: ListSortMode,
  locale: string
): ListRow[] {
  const collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
  const alphabetical = [...listEntries].sort((a, b) => collator.compare(getName(a), getName(b)));

  if (mode === "alphabetical") {
    return alphabetical.map((entry) => ({ kind: "entry", entry }));
  }

  const groupOrder = [...SUB_ASSET_GROUPS.map((g) => g.label), OTHER_SUB_ASSET_LABEL];
  const entriesByGroup = new Map<string, ListContent[]>(groupOrder.map((label) => [label, []]));
  alphabetical.forEach((entry) => entriesByGroup.get(getSubAssetGroup(entry))!.push(entry));

  // Ogni gruppo diventa [intestazione, ...voci] (o nulla se vuoto); flatMap unisce tutto in un'unica lista
  return groupOrder.flatMap((label): ListRow[] => {
    const groupEntries = entriesByGroup.get(label)!;
    if (groupEntries.length === 0) return [];
    return [
      { kind: "groupHeader", label, count: groupEntries.length },
      ...groupEntries.map((entry): ListRow => ({ kind: "entry", entry })),
    ];
  });
}
